import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Loader2,
  Lock,
  Unlock,
  Calendar,
  FileText,
  Clock,
  User,
  AlertTriangle,
  CheckCircle,
  FileSpreadsheet,
  History,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const CompetenciasPage = () => {
  const [loading, setLoading] = useState(true);
  const [competencias, setCompetencias] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  // Modal de reabertura
  const [showReabrirModal, setShowReabrirModal] = useState(false);
  const [reabrirCompetencia, setReabrirCompetencia] = useState(null);
  const [reabrirMotivo, setReabrirMotivo] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Modal de fechar
  const [showFecharModal, setShowFecharModal] = useState(false);
  const [fecharCompetencia, setFecharCompetencia] = useState(null);

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);
  
  const months = [
    { value: 1, label: 'Janeiro' },
    { value: 2, label: 'Fevereiro' },
    { value: 3, label: 'Março' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Maio' },
    { value: 6, label: 'Junho' },
    { value: 7, label: 'Julho' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Setembro' },
    { value: 10, label: 'Outubro' },
    { value: 11, label: 'Novembro' },
    { value: 12, label: 'Dezembro' },
  ];

  // Carregar competências
  const loadCompetencias = useCallback(async () => {
    setLoading(true);
    try {
      // Buscar competências existentes
      const res = await api.get(`/competencias?ano=${selectedYear}`);
      const existentes = res.data || [];
      
      // Criar lista completa de meses do ano
      const completas = months.map(m => {
        const existente = existentes.find(c => c.mes === m.value && c.ano === selectedYear);
        if (existente) {
          return existente;
        }
        return {
          mes: m.value,
          ano: selectedYear,
          status: 'aberta',
          fechada_em: null,
          fechada_por_nome: null,
          espelhos_gerados: false
        };
      });
      
      setCompetencias(completas);
    } catch (error) {
      console.error('Erro ao carregar competências:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedYear]);

  useEffect(() => {
    loadCompetencias();
  }, [loadCompetencias]);

  // Abrir modal de fechar
  const openFecharModal = (comp) => {
    setFecharCompetencia(comp);
    setShowFecharModal(true);
  };

  // Fechar competência
  const handleFechar = async () => {
    if (!fecharCompetencia) return;
    
    setSaving(true);
    try {
      const res = await api.post(`/competencias/${fecharCompetencia.ano}/${fecharCompetencia.mes}/fechar`);
      toast.success(res.data.message);
      if (res.data.espelhos_gerados > 0) {
        toast.info(`${res.data.espelhos_gerados} espelhos de ponto gerados`);
      }
      setShowFecharModal(false);
      loadCompetencias();
    } catch (error) {
      console.error('Erro ao fechar:', error);
      toast.error(error.response?.data?.detail || 'Erro ao fechar competência');
    } finally {
      setSaving(false);
    }
  };

  // Abrir modal de reabrir
  const openReabrirModal = (comp) => {
    setReabrirCompetencia(comp);
    setReabrirMotivo('');
    setShowReabrirModal(true);
  };

  // Reabrir competência
  const handleReabrir = async () => {
    if (!reabrirCompetencia || !reabrirMotivo.trim()) {
      toast.error('Informe o motivo da reabertura');
      return;
    }
    
    setSaving(true);
    try {
      const res = await api.post(`/competencias/${reabrirCompetencia.ano}/${reabrirCompetencia.mes}/reabrir`, {
        motivo: reabrirMotivo
      });
      toast.success(res.data.message);
      setShowReabrirModal(false);
      loadCompetencias();
    } catch (error) {
      console.error('Erro ao reabrir:', error);
      toast.error(error.response?.data?.detail || 'Erro ao reabrir competência');
    } finally {
      setSaving(false);
    }
  };

  // Formatar data
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const d = new Date(dateStr);
      return d.toLocaleString('pt-BR', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  // Determinar status do mês (passado, atual, futuro)
  const getMonthStatus = (mes, ano) => {
    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear();
    
    if (ano < currentYear || (ano === currentYear && mes < currentMonth)) {
      return 'passado';
    } else if (ano === currentYear && mes === currentMonth) {
      return 'atual';
    }
    return 'futuro';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="competencias-page">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Lock className="w-6 h-6 text-blue-600" />
            Fechamento de Competência
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Feche o mês para gerar espelhos de ponto e bloquear edições
          </p>
        </div>

        {/* Filtros */}
        <div className="flex flex-wrap items-center gap-2 bg-slate-50 dark:bg-slate-800/50 p-3 rounded-lg">
          <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
            <SelectTrigger className="w-24 bg-white dark:bg-slate-700">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map((y) => (
                <SelectItem key={y} value={String(y)}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Aviso */}
      <Card className="bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-amber-800 dark:text-amber-300 mb-1">
                Importante sobre o fechamento de competência:
              </p>
              <ul className="text-amber-700 dark:text-amber-400 space-y-1 list-disc list-inside text-xs">
                <li>Ao fechar, os dados do mês são congelados e os espelhos de ponto são gerados.</li>
                <li>Edições de apontamentos, abonos ou classificações ficam bloqueadas.</li>
                <li>Para alterar, é necessário reabrir a competência (registrado com motivo).</li>
                <li>Todas as alterações são auditadas com usuário e data/hora.</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Competências */}
      <Card className="dark:bg-slate-800/50 dark:border-slate-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Calendar className="w-4 h-4 text-blue-500" />
            Competências de {selectedYear}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-100 dark:bg-slate-900/50">
                  <TableHead className="font-bold">Competência</TableHead>
                  <TableHead className="font-bold">Status</TableHead>
                  <TableHead className="font-bold">Fechado em</TableHead>
                  <TableHead className="font-bold">Por</TableHead>
                  <TableHead className="font-bold">Espelhos</TableHead>
                  <TableHead className="font-bold">Reabertura</TableHead>
                  <TableHead className="font-bold text-center">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {competencias.map((comp) => {
                  const monthStatus = getMonthStatus(comp.mes, comp.ano);
                  const isFechada = comp.status === 'fechada';
                  
                  // Meses em implantação (Jan-Abr 2026) - sistema ainda não estava ativo
                  const isEmImplantacao = comp.ano === 2026 && comp.mes >= 1 && comp.mes <= 4;
                  
                  return (
                    <TableRow 
                      key={`${comp.ano}-${comp.mes}`} 
                      className={`${
                        isEmImplantacao ? 'bg-slate-100 dark:bg-slate-900/50 opacity-60' :
                        monthStatus === 'atual' ? 'bg-blue-50 dark:bg-blue-900/10' :
                        monthStatus === 'futuro' ? 'opacity-50' : ''
                      }`}
                    >
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <span className={isEmImplantacao ? 'text-slate-400' : ''}>{months.find(m => m.value === comp.mes)?.label}/{comp.ano}</span>
                          {isEmImplantacao && (
                            <Badge className="bg-slate-200 text-slate-500 text-[10px] dark:bg-slate-700 dark:text-slate-400">
                              Em Implantação
                            </Badge>
                          )}
                          {!isEmImplantacao && monthStatus === 'atual' && (
                            <Badge className="bg-blue-100 text-blue-700 text-[10px]">
                              Atual
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {isEmImplantacao ? (
                          <Badge className="bg-slate-200 text-slate-500 dark:bg-slate-700 dark:text-slate-400">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            N/A
                          </Badge>
                        ) : isFechada ? (
                          <Badge className="bg-green-100 text-green-700">
                            <Lock className="w-3 h-3 mr-1" />
                            Fechada
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700">
                            <Unlock className="w-3 h-3 mr-1" />
                            Aberta
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">
                        {formatDateTime(comp.fechada_em)}
                      </TableCell>
                      <TableCell className="text-sm">
                        {comp.fechada_por_nome || '-'}
                      </TableCell>
                      <TableCell>
                        {comp.espelhos_gerados ? (
                          <Badge className="bg-green-100 text-green-700 text-[10px]">
                            <FileSpreadsheet className="w-3 h-3 mr-1" />
                            Gerados
                          </Badge>
                        ) : (
                          <span className="text-slate-400 text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">
                        {comp.reaberta_em ? (
                          <div className="space-y-0.5">
                            <div className="flex items-center gap-1">
                              <History className="w-3 h-3" />
                              {formatDateTime(comp.reaberta_em)}
                            </div>
                            <div>por {comp.reaberta_por_nome}</div>
                            <div className="text-amber-600 truncate max-w-[150px]" title={comp.reaberta_motivo}>
                              Motivo: {comp.reaberta_motivo}
                            </div>
                          </div>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {isEmImplantacao ? (
                          <span className="text-xs text-slate-400 italic">Dados não disponíveis</span>
                        ) : monthStatus !== 'futuro' && (
                          isFechada ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openReabrirModal(comp)}
                              className="text-amber-600 border-amber-300 hover:bg-amber-50"
                            >
                              <Unlock className="w-4 h-4 mr-1" />
                              Reabrir
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openFecharModal(comp)}
                              className="text-green-600 border-green-300 hover:bg-green-50"
                            >
                              <Lock className="w-4 h-4 mr-1" />
                              Fechar
                            </Button>
                          )
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Modal de Fechar */}
      <Dialog open={showFecharModal} onOpenChange={setShowFecharModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-green-600" />
              Fechar Competência
            </DialogTitle>
            <DialogDescription>
              {fecharCompetencia && (
                <span>
                  Você está prestes a fechar a competência de{' '}
                  <strong>{months.find(m => m.value === fecharCompetencia.mes)?.label}/{fecharCompetencia.ano}</strong>.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h4 className="font-medium text-amber-800 flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4" />
                O que acontece ao fechar:
              </h4>
              <ul className="text-sm text-amber-700 space-y-1 list-disc list-inside">
                <li>Os dados do mês serão congelados</li>
                <li>Espelhos de ponto serão gerados para cada CLT</li>
                <li>Edições ficarão bloqueadas até reabertura</li>
              </ul>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFecharModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleFechar} 
              disabled={saving}
              className="bg-green-600 hover:bg-green-700"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
              Confirmar Fechamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Reabrir */}
      <Dialog open={showReabrirModal} onOpenChange={setShowReabrirModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Unlock className="w-5 h-5 text-amber-600" />
              Reabrir Competência
            </DialogTitle>
            <DialogDescription>
              {reabrirCompetencia && (
                <span>
                  Você está prestes a reabrir a competência de{' '}
                  <strong>{months.find(m => m.value === reabrirCompetencia.mes)?.label}/{reabrirCompetencia.ano}</strong>.
                  É necessário informar o motivo.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label>Motivo da Reabertura *</Label>
              <Textarea
                value={reabrirMotivo}
                onChange={(e) => setReabrirMotivo(e.target.value)}
                placeholder="Descreva o motivo para reabrir esta competência..."
                rows={3}
              />
              <p className="text-xs text-slate-500">
                Este motivo ficará registrado no histórico de auditoria.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReabrirModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleReabrir} 
              disabled={saving || !reabrirMotivo.trim()}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Unlock className="w-4 h-4 mr-2" />}
              Confirmar Reabertura
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompetenciasPage;
